import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getMonthCompare, reportDealsHref, type MonthBucket, type ReportDealsMetric } from '../api/reports';
import { Icon } from './Icon';
import { formatMoney } from '../utils/money';

/**
 * Build 10 (Daniel 2026-09-08): this month against last month, on Home.
 *
 * "have a this month vs last month as well so we can see how things are
 * stacking up at this time."
 *
 * The comparison that matters on the 8th is last month THROUGH the 8th,
 * not last month's whole total, so that is the default. The toggle flips to
 * the full month, and a second toggle swaps last month for the same month
 * last year. A pace line at the bottom says where the month lands if every
 * deal with a close date left in the month actually closes.
 */
export default function MonthCompareCard() {
  const { data } = useQuery({ queryKey: ['month-compare'], queryFn: getMonthCompare, staleTime: 60_000 });
  const [against, setAgainst] = useState<'last_month' | 'year_ago'>('last_month');
  const [span, setSpan] = useState<'to_date' | 'full'>('to_date');
  if (!data) return null;

  const cur = data.this_month;
  const cmp: MonthBucket = against === 'last_month'
    ? (span === 'to_date' ? data.last_month_to_date : data.last_month_full)
    : (span === 'to_date' ? data.year_ago_to_date : data.year_ago_full);
  const scopeNote = data.scope === 'own' ? 'your deals' : 'team';

  const rows: Array<{ label: string; cur: number; prev: number; money?: boolean; lowerIsBetter?: boolean; metric?: ReportDealsMetric }> = [
    { label: 'Closed', cur: cur.closed_units, prev: cmp.closed_units, metric: 'closed' },
    { label: 'Closed volume', cur: cur.closed_volume_cents, prev: cmp.closed_volume_cents, money: true, metric: 'closed' },
    ...(data.commission_visible && cur.net_to_company_cents !== null && cmp.net_to_company_cents !== null
      ? [{ label: 'Net to company', cur: cur.net_to_company_cents, prev: cmp.net_to_company_cents, money: true, metric: 'deals' as const }]
      : []),
    { label: 'Contracts written', cur: cur.contracts_written, prev: cmp.contracts_written, metric: 'contracts_written' },
    { label: 'Listings taken', cur: cur.listings_taken, prev: cmp.listings_taken, metric: 'listings_taken' },
    { label: 'New leads', cur: cur.new_leads, prev: cmp.new_leads },
    { label: 'Appointments set', cur: cur.appointments_set, prev: cmp.appointments_set },
  ];

  const pace = data.pace;
  // Build 92: each deal number opens the deals behind it, for that month.
  const hrefFor = (b: MonthBucket, metric?: ReportDealsMetric) => (metric
    ? reportDealsHref({ preset: 'custom', from: b.start_ymd, to: b.end_ymd, date_field: 'close', compare: 'none' }, { metric })
    : undefined);
  const linkStyle = { color: 'inherit', textDecoration: 'none' } as const;

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-sm)',
        padding: 18,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)' }}><Icon name="calendar" size={15} /></span>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text-heading)', margin: 0, letterSpacing: '-0.01em' }}>
          {cur.label} vs. {cmp.label}
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {scopeNote}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Toggle
            value={against}
            onChange={setAgainst}
            options={[{ v: 'last_month', l: 'Last month' }, { v: 'year_ago', l: 'Last year' }]}
          />
          <Toggle
            value={span}
            onChange={setSpan}
            options={[{ v: 'to_date', l: `Through the ${ordinal(data.day_of_month)}` }, { v: 'full', l: 'Full month' }]}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px 16px' }}>
        {rows.map((r) => {
          const d = delta(r.cur, r.prev, r.lowerIsBetter);
          const color = d.good === true ? '#196B47' : d.good === false ? '#A32D2D' : 'var(--text-muted)';
          return (
            <div key={r.label} style={{ padding: '8px 0', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>{r.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-heading)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                  {r.metric
                    ? <Link to={hrefFor(cur, r.metric)!} style={linkStyle} title="See the deals behind this number">{r.money ? formatMoney(r.cur) : r.cur}</Link>
                    : (r.money ? formatMoney(r.cur) : r.cur)}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color }}>{d.text}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {r.metric
                  ? <Link to={hrefFor(cmp, r.metric)!} style={linkStyle} title="See the deals behind this number">{r.money ? formatMoney(r.prev) : r.prev} {against === 'last_month' ? 'last month' : 'last year'}</Link>
                  : <>{r.money ? formatMoney(r.prev) : r.prev} {against === 'last_month' ? 'last month' : 'last year'}</>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pace: closed so far plus what is still on the calendar this month. */}
      <div
        style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid var(--border)',
          fontSize: 12,
          color: 'var(--text-muted)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span>
          Pace: {cur.closed_units} closed so far plus {pace.scheduled_units} scheduled in the next {pace.days_left} day{pace.days_left === 1 ? '' : 's'}
          {' '}puts {cur.label.split(' ')[0]} at{' '}
          <strong style={{ color: 'var(--text-heading)' }}>{pace.projected_units} closing{pace.projected_units === 1 ? '' : 's'}</strong>
          {' and '}
          <strong style={{ color: 'var(--text-heading)' }}>{formatMoney(pace.projected_volume_cents)}</strong>
          {pace.projected_net_to_company_cents !== null && (
            <> ({formatMoney(pace.projected_net_to_company_cents)} net to company)</>
          )}
          {' '}if every scheduled deal closes.
        </span>
        <Link to="/reports?preset=this_month" style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}>
          Full report
        </Link>
      </div>
    </section>
  );
}

function delta(cur: number, prev: number, lowerIsBetter = false): { text: string; good: boolean | null } {
  if (prev === 0) return cur === 0 ? { text: 'flat', good: null } : { text: 'from zero', good: lowerIsBetter ? false : true };
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return { text: 'flat', good: null };
  return { text: `${pct > 0 ? '+' : ''}${pct}%`, good: lowerIsBetter ? pct < 0 : pct > 0 };
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function Toggle<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<{ v: T; l: string }> }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 999, overflow: 'hidden' }}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          style={{
            padding: '4px 10px',
            fontSize: 11,
            fontFamily: 'inherit',
            cursor: 'pointer',
            border: 'none',
            background: value === o.v ? 'var(--brand-primary-soft)' : 'var(--bg-surface)',
            color: value === o.v ? 'var(--text-heading)' : 'var(--text-muted)',
            fontWeight: value === o.v ? 600 : 500,
          }}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}
